import { html, type Html, View } from 'rune-ts';
import klass from './AbstractFieldGroupView.module.scss';
import { font } from '../../../../../shared/typography';

export interface IFieldGroupStyles {
  field_group?: string;
  label_wrap?: string;
  label?: string;
  required?: string;
  error?: string;
  body?: string;
}

export abstract class AbstractFieldGroupView<Value> extends View<{
  value: Value;
}> {
  protected readonly label: string;
  protected required?: boolean;

  private readonly styles: IFieldGroupStyles;
  private visible: boolean;

  protected constructor(
    value: Value,
    label: string,
    required?: boolean,
    styles?: IFieldGroupStyles,

    /**
     * initial visibility
     */
    visible?: boolean,
  ) {
    super({ value });

    this.label = label;
    this.required = required;
    this.styles = styles ?? {
      field_group: `${klass.field_group}`,
      label_wrap: `${klass.label_wrap} ${font('14_medium')}`,
      label: klass.label,
      required: klass.required,
    };
    this.visible = visible ?? true;
  }

  protected abstract body(): Html | View;

  override template() {
    return html`
      <div class="${this.styles.field_group} ${this.visible ? '' : klass.hidden}">
        <span class="${this.styles.label_wrap}">
          <label class="${this.styles.label}">${this.label}</label>
          <span class="${this.styles.required} ${this.required ? '' : klass.hidden}">*</span>
        </span>
        <div class="${this.styles.body}">${this.body()}</div>
      </div>
    `;
  }

  public show(): this {
    if (!this.visible) {
      this.visible = true;
      this.syncVisible();
    }
    return this;
  }

  private syncVisible() {
    if (this.visible) {
      this.element().classList.remove(klass.hidden);
    } else {
      this.element().classList.add(klass.hidden);
    }
  }

  public setRequired(required: boolean): this {
    this.required = required;
    return this;
  }

  public isRequired(): boolean {
    return this.required ?? false;
  }

  public setRequiredAndSync(required: boolean): this {
    return this.setRequired(required).syncRequired();
  }

  private syncRequired(): this {
    const require_el = this.element().querySelector(`.${this.styles.required}`);
    if (require_el) {
      if (this.isRequired()) {
        require_el.classList.remove(klass.hidden);
      } else {
        require_el.classList.add(klass.hidden);
      }
    }
    return this;
  }
}
